import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrlWithQuery } from '@routes/util';
import { PieChart, Pie, Cell, Label } from 'recharts';
import { Card, CardContent, CardTitle, CardHeader } from '@g17eco/ui-modern/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@g17eco/ui-modern/components/ui/chart';
import { useGetEmissionsByActivityTypeQuery } from '../api/emissions';
import type { ActivityTypeReport } from '../types/emissions';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { convertEmissionValue } from '../utils/emissionsFormatter';
import { ACTIVITY_TYPE_COLORS, ACTIVITY_TYPE_FA_ICONS, formatActivityType } from '../config/activityTypeBranding';

interface CategoryBreakdownPanelProps {
  initiativeId: string;
  periodStart?: string;
  periodEnd?: string;
  includeChildren?: boolean;
}

interface CategoryData {
  activityType: string;
  name: string;
  /** Raw kg value for passing to the formatter */
  emissionsKg: number;
  /** Converted value for Recharts chart data (in org display unit) */
  emissions: number;
  color: string;
}

const transformToCategories = (reports: ActivityTypeReport[], convertValue: (kg: number) => number): CategoryData[] => {
  return reports
    .map((report, index) => ({
      activityType: report.activityType,
      name: formatActivityType(report.activityType),
      emissionsKg: report.total,
      emissions: convertValue(report.total),
      color: ACTIVITY_TYPE_COLORS[report.activityType] || `hsl(${(index * 35) + 15}, 85%, 45%)`,
    }))
    .sort((a, b) => b.emissions - a.emissions);
};

export const CategoryBreakdownPanel = ({
  initiativeId,
  periodStart,
  periodEnd,
  includeChildren,
}: CategoryBreakdownPanelProps) => {
  const { settings, formatCompact } = useEmissionsFormatter();
  const history = useHistory();
  const defaultStart = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 11);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  }, []);

  const defaultEnd = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  }, []);

  const { data: activityTypeData, isLoading } = useGetEmissionsByActivityTypeQuery(
    {
      initiativeId,
      periodStart: periodStart || defaultStart,
      periodEnd: periodEnd || defaultEnd,
      includeChildren,
    },
    { skip: !initiativeId }
  );

  const categories = useMemo(() => {
    if (!activityTypeData || activityTypeData.length === 0) {
      return [];
    }
    return transformToCategories(activityTypeData, (kg) => convertEmissionValue(kg, settings.displayUnit));
  }, [activityTypeData, settings.displayUnit]);

  const MAX_CATEGORIES = 5;
  const OTHER_COLOR = '#94a3b8';

  const total = categories.reduce((sum, cat) => sum + cat.emissions, 0);
  const totalKg = categories.reduce((sum, cat) => sum + cat.emissionsKg, 0);

  const displayCategories = useMemo(() => {
    if (categories.length <= MAX_CATEGORIES) {
      return categories;
    }
    const top = categories.slice(0, MAX_CATEGORIES);
    const rest = categories.slice(MAX_CATEGORIES);
    const otherKg = rest.reduce((sum, cat) => sum + cat.emissionsKg, 0);
    const otherConverted = rest.reduce((sum, cat) => sum + cat.emissions, 0);
    top.push({
      activityType: '__other__',
      name: 'Other',
      emissionsKg: otherKg,
      emissions: otherConverted,
      color: OTHER_COLOR,
    });
    return top;
  }, [categories]);

  const chartItems = useMemo(
    () => displayCategories.map((cat) => ({ name: cat.name, emissions: cat.emissions, fill: cat.color })),
    [displayCategories]
  );

  const hasData = categories.length > 0;
  const viewAllUrl = generateUrlWithQuery(
    emissionsTrackerRoutes.EMISSIONS_INSIGHTS,
    { rootAppPath, initiativeId },
    { tab: 'sources' },
  );
  const viewAllLink = (
    <Button
      variant='link'
      size='sm'
      onClick={() => history.push(viewAllUrl)}
    >
      View all
      <ArrowRight />
    </Button>
  );

  if (isLoading) {
    return (
      <div className='tw:flex tw:items-center tw:justify-center tw:py-10'>
        <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
      </div>
    );
  }

  if (!hasData) {
    return (
      <Card>
        <CardContent className='tw:p-4 text-center'>
          <i className='fa fa-chart-pie fa-2x text-ThemeTextLight mb-2 opacity-50' />
          <p className='text-ThemeTextLight mb-0'>No category data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6 tw:h-full'>
      {/* Pie Chart */}
      <Card className='tw:h-full'>
        <CardHeader>
          <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
            <CardTitle>Emissions by Category</CardTitle>
            {viewAllLink}
          </div>
        </CardHeader>
        <CardContent>
          <ChartContainer config={{}} className='tw:w-full' style={{ height: 200 }}>
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <Pie
                data={chartItems}
                dataKey='emissions'
                nameKey='name'
                innerRadius={55}
                outerRadius={80}
                strokeWidth={3}
              >
                {displayCategories.map((cat) => (
                  <Cell key={cat.name} fill={cat.color} />
                ))}
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      const totalFormatted = formatCompact(totalKg);
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor='middle' dominantBaseline='middle'>
                          <tspan x={viewBox.cx} y={viewBox.cy} className='tw:fill-foreground tw:text-xl tw:font-bold'>
                            {totalFormatted.formatted}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 16} className='tw:fill-muted-foreground tw:text-xs'>
                            {totalFormatted.unit}
                          </tspan>
                        </text>
                      );
                    }
                    return null;
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
          <div className='tw:flex tw:flex-wrap ttw:justify-center tw:gap-2 tw:mt-0'>
            {displayCategories.map((cat) => (
              <div key={cat.name} className='tw:flex tw:items-center tw:gap-2'>
                <span className='tw:rounded-full tw:shrink-0' style={{ width: 10, height: 10, backgroundColor: cat.color }} />
                <span className='tw:text-xs'>{cat.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Category Details Table */}
      <Card className='tw:h-full'>
        <CardHeader>
          <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
            <CardTitle>Category Details</CardTitle>
            {viewAllLink}
          </div>
        </CardHeader>
        <CardContent>
          {displayCategories.map((cat) => {
            const percentage = ((cat.emissions / total) * 100);
            const percentageStr = percentage.toFixed(0);
            return (
              <div key={cat.name} className='tw:py-2'>
                <div className='tw:flex tw:justify-between tw:items-center tw:text-xs tw:mb-2'>
                  <div className='tw:flex tw:items-center tw:gap-2'>
                    <div
                      className='tw:rounded tw:flex tw:items-center tw:justify-center tw:shrink-0'
                      style={{ width: 22, height: 22, backgroundColor: `${cat.color}22` }}
                    >
                      <i
                        className={`fa ${ACTIVITY_TYPE_FA_ICONS[cat.activityType] ?? 'fa-leaf'}`}
                        style={{ color: cat.color }}
                      />
                    </div>
                    <div>{cat.name}</div>
                  </div>
                  <div className='tw:text-right text-nowrap'>
                    <span className='tw:font-bold tw:mr-2'>{formatCompact(cat.emissionsKg).display}</span>
                    <span className='text-ThemeTextLight'>{percentageStr}%</span>
                  </div>
                </div>
                <Progress
                  value={percentage}
                  className='tw:h-2'
                  progressColor={cat.color}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};
